import {
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { Pencil1Icon } from '@radix-ui/react-icons';
import { FieldHookConfig, FieldInputProps, useField, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { colors, styled } from '../Stitches';
import useInitialFocus from './hooks/useInitialFocus';

// Visual Components
const StyledInlineInputText = styled(Heading, {
  padding: '$3',
  borderRadius: '$3',
  '&:hover': { px: '$4' },

  transitionProperty: 'all',
  transitionDuration: '100ms',

  cursor: 'pointer',

  variants: {
    colorMode: {
      dark: {
        '&:hover': {
          backgroundColor: '$whiteAlpha100Dark',
          boxShadow: '0px 0px 0px 1px #2a2d3a inset',
        },
      },
      light: {
        '&:hover': { backgroundColor: '$gray200', boxShadow: '0px 0px 0px 1px #2a2d3a inset' },
      },
    },
  },
});

const EditButton = styled(Pencil1Icon, {
  display: 'inline',
  marginLeft: '$3',
});

const InlineInputText = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const { colorMode } = useColorMode();
  return (
    <StyledInlineInputText onClick={onClick} colorMode={colorMode} size="md">
      {children}
      <EditButton />
    </StyledInlineInputText>
  );
};

const useInlineInputStyles = () => {
  const { colorMode } = useColorMode();

  return {
    size: 'sm',

    backgroundColor: colorMode === 'light' ? undefined : colors.dark.whiteAlpha100,

    fontFamily: 'heading',
    fontWeight: '700',
    fontSize: '1.1em',

    borderRadius: 4,
  };
};

type InlineInputFieldProps = FieldInputProps<string> & {
  onKeyDown: (e: React.KeyboardEvent<any>) => void;
};

/** Inline Input Field is styled and automatically draws focus on initialisation */
const InlineInputField = ({ onKeyDown, ...props }: InlineInputFieldProps) => {
  const focusRef = useInitialFocus<HTMLInputElement>();
  const styleProps = useInlineInputStyles();

  return <Input {...styleProps} {...props} ref={focusRef} onKeyDown={onKeyDown} />;
};

const InlineInput = ({ onExit, ...props }: FieldHookConfig<string> & { onExit: () => void }) => {
  const [field, meta, { setValue }] = useField(props);
  const { resetForm, initialValues, submitForm } = useFormikContext<any>();

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !meta.error) {
      submitForm();
      onExit();
    }
  };

  const onBlur = () => {
    resetForm({ values: initialValues });
    onExit();
  };

  const errorBackground: any = useColorModeValue('whiteAlpha.900', colors.dark.darkAlpha900);

  return (
    <FormControl isInvalid={!!meta.error}>
      <InlineInputField
        {...field}
        onChange={(e: any) => setValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      <FormErrorMessage
        backgroundColor={errorBackground}
        position="absolute"
        top={8}
        p={1}
        borderRadius={1}
      >
        {meta.error}
      </FormErrorMessage>
    </FormControl>
  );
};

export const InlineField = (props: FieldHookConfig<string>) => {
  const [show, setShow] = useState(false);
  const [{ value }] = useField(props);

  const toggleShow = () => setShow(!show);
  const onExit = () => setShow(false);

  if (show) {
    return <InlineInput onExit={onExit} {...props} />;
  } else {
    return <InlineInputText onClick={toggleShow}>{value}</InlineInputText>;
  }
};
